import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Minus } from 'lucide-react';
import headerBg from '../../assets/8.jpg';

const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 min-w-0">
    {Icon && (
      <div className="p-2 bg-slate-50 rounded-xl text-slate-400 shrink-0 border border-slate-100/50">
        <Icon className="w-4 h-4" />
      </div>
    )}
    {/* Developer : nithin 
    date: 24/06/2026
    changes : Added Tool Tip        */}
    <div className="min-w-0 pt-0.5">
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{label}</div>
      <div
        className="text-[13px] font-semibold text-slate-800 truncate leading-snug"
        title={typeof value === 'string' || typeof value === 'number' ? String(value) : undefined}
      >
        {value || '—'}
      </div>
    </div>
  </div>
);

export const DetailsWorkspaceModal = ({
  isOpen,
  onClose,
  title = "Verve Kitchens",
  subtitle = "Details Panel",
  fields = [], // Array of { icon: LucideIcon, label: String, value: String }
  timeline = [], // Array of String timeline events
  onAddNote, // Callback: (draftText) => Promise/void
  isInactive = false,
  statusLabel, // e.g. "Disable Profile" or "Enable Profile"
  statusColor = "rose", // "rose" | "emerald" | "slate"
  onStatusToggle, // Callback when status button clicked
  saving = false,
  savingNote = false,
  hideSidebar = false,
  maxWidthClass,
  closeIcon = "close", // "close" | "minimize"
  children, // Optional custom content below info grid
}) => {
  const [noteDraft, setNoteDraft] = useState('');

  if (!isOpen) return null;

  const handleAddNoteClick = async () => {
    const text = noteDraft.trim();
    if (!text || !onAddNote) return;
    try {
      await onAddNote(text);
      setNoteDraft('');
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusBtnClass = () => {
    if (statusColor === 'rose') {
      return 'text-rose-300 hover:text-white bg-rose-500/10 hover:bg-rose-500 border border-rose-500/20';
    }
    if (statusColor === 'emerald') {
      return 'text-emerald-300 hover:text-white bg-emerald-500/10 hover:bg-emerald-500 border border-emerald-500/20';
    }
    return 'text-slate-300 hover:text-white bg-slate-500/10 hover:bg-slate-500 border border-slate-500/20';
  };

  return createPortal(
    <div className="fixed inset-0 z-[50] flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
      {/* Deep, clean backdrop */}
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden />

      {/* Popover card */}
      <div className={`relative z-[51] w-full ${maxWidthClass || (hideSidebar ? 'max-w-2xl' : 'max-w-3xl')} bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden border border-slate-100 max-h-[90vh]`}>

        {/* Header Banner - Matching Add Forms perfectly */}
        <div
          className="px-5 py-4 flex items-center justify-between text-white shrink-0 bg-cover bg-center relative overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url(${headerBg})`
          }}
        >
          <div className="flex flex-col gap-0.5 z-10 relative">
            <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
              {title}
            </span>
            <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
              {subtitle}
            </span>
          </div>

          {/* Quick Actions in Header */}
          <div className="flex items-center gap-3 z-10 relative">
            {statusLabel && onStatusToggle && (
              <button
                type="button"
                onClick={onStatusToggle}
                disabled={saving}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer disabled:opacity-40 ${getStatusBtnClass()}`}
              >
                {statusLabel}
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-all cursor-pointer"
              title={closeIcon === 'minimize' ? "Minimize View" : "Close"}
            >
              {closeIcon === 'minimize' ? <Minus className="w-5 h-5" /> : <X className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Columns Section */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden relative bg-white">
          {/* Disabled Gray Overlay */}
          {isInactive && (
            <div
              className="absolute inset-0 z-[5] bg-slate-200/25 backdrop-blur-[0.5px] pointer-events-auto"
              aria-hidden
            />
          )}

          {/* Left Side: Clean details grid (two fields per row) */}
          <div className="flex-1 overflow-y-auto p-6 sm:p-7 min-w-0">
            <h4 className="text-[11px] font-bold text-slate-400 tracking-wider mb-4 uppercase">
              Information Details
            </h4>

            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {fields.map((f, idx) => (
                <DetailItem key={idx} icon={f.icon} label={f.label} value={f.value} />
              ))}
            </div>

            {children && (
              <div className="mt-8 pt-6 border-t border-slate-100">
                {children}
              </div>
            )}
          </div>

          {/* Right Side: Timeline Notes Panel */}
          {!hideSidebar && (
            <div className="w-full md:w-[280px] lg:w-[320px] flex flex-col shrink-0 bg-slate-50 border-t md:border-t-0 md:border-l border-slate-200/80 shadow-[-10px_0_30px_rgba(0,0,0,0.025)] relative z-10 overflow-hidden">

              {/* Add Note input wrapper */}
              {onAddNote && (
                <div className="p-5 border-b border-slate-100/80 shrink-0 bg-white/50 backdrop-blur-sm">
                  <h4 className="text-[11px] font-bold text-slate-400 tracking-wider mb-3 uppercase">
                    Add Activity Update
                  </h4>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Type a new update..."
                      disabled={isInactive}
                      className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 placeholder:text-slate-400 bg-white focus:outline-none focus:border-slate-400 transition-all disabled:bg-slate-100 disabled:text-slate-400 shadow-sm"
                    />
                    <button
                      type="button"
                      title="Save note"
                      onClick={handleAddNoteClick}
                      disabled={isInactive || !noteDraft.trim() || savingNote}
                      className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-all disabled:opacity-30 cursor-pointer shadow-sm"
                    >
                      {savingNote ? '...' : <Plus className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              {/* Timeline Log */}
              <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
                <h4 className="text-[11px] font-bold text-slate-400 tracking-wider mb-4 uppercase">
                  Activity Timeline
                </h4>
                <div className="flex flex-col border-l border-slate-200/80 pl-4 ml-1">
                  {timeline.map((line, idx) => (
                    <div key={idx} className="relative pb-5 last:pb-0">
                      <span className="absolute -left-[20.5px] top-1.5 w-2.5 h-2.5 rounded-full bg-amber-400 outline outline-4 outline-slate-50" />
                      <p className="text-xs font-medium text-slate-600 break-words leading-relaxed">{line}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>,
    document.body
  );
};

export default DetailsWorkspaceModal;
